<template>
  <div class="universal-passcode-bg" :style="containerStyle" style="outline: none">
    <div class="error-msg-area">
      <alert-msg v-if="backendError" :error-msg="backendErrorMsg" component="passcode" />
    </div>
    <div class="universal-passcode-title" :style="titleStyle">{{ title }}</div>
    <div class="passcode-container">
      <div
        class="universal-passcode-dots"
        :class="{ shake: shakeDots }"
        :style="dotsContainerStyle"
      >
        <div
          v-for="index in passcodeLength"
          :key="`dot-${index}-${enteredPasscode.length}`"
          class="universal-dot"
          :class="{ filled: enteredPasscode.length >= index }"
          :style="scaledDotStyle"
        />
      </div>
      <div class="error-message" :class="{ show: invalidPasscode }">
        {{ invalidPasscodeMSG }}
      </div>
    </div>
    <div class="universal-keypad" :style="keypadStyle">
      <div v-for="(row, rowIndex) in keypadRows" :key="row.join('-')" class="universal-keypad-row">
        <div
          v-for="(num, numIndex) in row"
          :key="`keypad-${rowIndex}-${numIndex}-${num !== null ? num : 'empty'}`"
        >
          <div
            v-if="num !== null"
            class="universal-key"
            :class="{ pressed: pressedNumber === num }"
            :aria-label="`Digit ${num}`"
            :style="getKeypadButtonStyle(num)"
            @click="handleNumberClick(num)"
            @mousedown="handlePress(num)"
            @mouseup="handleRelease(num)"
            @mouseenter="handleHover(num)"
            @mouseleave="handleHoverLeave(num)"
          >
            <div class="universal-key-number" :style="keypadButtonTextStyle">{{ num }}</div>
          </div>
          <div v-else class="universal-key empty" />
        </div>
      </div>
    </div>
    <div :style="btnGroupStyle" class="btn-group">
      <button
        class="social-btn connect"
        :style="primaryButtonStyles"
        :disabled="loading"
        @click.prevent="connect()"
      >
        <template v-if="!loading">
          <i class="fa fa-wifi" aria-hidden="true" />
          {{ $t('connect_button') }}
        </template>
        <template v-else>
          <div class="sk-three-bounce">
            <div class="sk-child sk-bounce1" />
            <div class="sk-child sk-bounce2" />
            <div class="sk-child sk-bounce3" />
          </div>
        </template>
      </button>
      <a v-if="baseData.backButton" href="#" @click.prevent="goBack()">← {{ $t('back_button') }}</a>
    </div>
  </div>
</template>

<script>
  import { required } from 'vuelidate/lib/validators'
  import AlertMsg from '../../components/errors/alert.vue'
  import axios from 'axios'
  import qs from 'qs'
  const touchMap = new WeakMap()
  export default {
    components: {
      AlertMsg,
    },
    props: {
      api: { type: String, required: true },
      wftkn: { type: String, required: true },
      action: { type: String, required: true },
      accentColor: { type: String, default: '#1976d2' },
      buttonBackground: { type: String, default: '' },
      buttonTextColor: { type: String, default: '' },
      buttonBorderColor: { type: String, default: '' },
      title: { type: String, default: 'Enter Passcode' },
    },
    data() {
      return {
        passcode: '',
        loading: false,
        connected: false,
        validating: false,
        backendError: false,
        backendErrorMsg: '',
        enteredPasscode: [],
        pressedNumber: null,
        hoveredNumber: null,
        passcodeLength: 6,
        invalidPasscode: false,
        invalidPasscodeMSG: '',
        shakeDots: false,
        keypadRows: [
          [1, 2, 3],
          [4, 5, 6],
          [7, 8, 9],
          [null, 0, null],
        ],
        scaleFactor: 1,
        viewportHeight: window.innerHeight,
      }
    },
    computed: {
      allValidated() {
        return !this.$v.$invalid
      },
      baseData() {
        return this.$store.state.baseData
      },
      primaryButtonStyles() {
        const scaledRadius = Math.round((this.radius || 32) * this.scaleFactor)
        const scaledPadding = Math.round(12 * this.scaleFactor)
        const scaledFontSize = Math.round(16 * this.scaleFactor)
        return {
          'border-radius': `${scaledRadius}px`,
          background: this.primaryButtonColor || '#55acee',
          color: this.primaryButtonTextColor || 'white',
          'justify-content': this.primaryButtonTextAlignment || 'center',
          padding: `${scaledPadding}px ${scaledPadding * 2}px`,
          fontSize: `${scaledFontSize}px`,
        }
      },
      radius() {
        return this.baseData.sizes.loginBtnsRadius
      },
      primaryButtonColor() {
        return this.baseData.colors.primaryBtn
      },
      primaryButtonTextColor() {
        return this.baseData.colors.primaryBtnText
      },
      primaryButtonTextAlignment() {
        return this.baseData.fonts.alignments.primaryBtn
      },
      dotStyle() {
        return {
          borderColor: '#666',
          color: '#888',
        }
      },
      scaledDotStyle() {
        // Static size - no scaling
        return {
          ...this.dotStyle,
          width: '14px',
          height: '14px',
          borderWidth: '2px',
        }
      },
      titleStyle() {
        // Static size - no scaling, reduced margin bottom to minimize spacing
        return {
          fontSize: '22.4px',
          marginBottom: '4px',
          letterSpacing: '1px',
        }
      },
      dotsContainerStyle() {
        // Static gap - no scaling, reduced margin bottom to minimize spacing
        return {
          gap: '18px',
          marginBottom: '4px',
        }
      },
      keypadStyle() {
        const scaledGap = Math.round(12 * this.scaleFactor)
        return {
          gap: `${scaledGap}px`,
        }
      },
      btnGroupStyle() {
        const scaledMarginTop = Math.round(16 * this.scaleFactor)
        return {
          position: 'relative',
          marginTop: `${scaledMarginTop}px`,
        }
      },
      containerStyle() {
        const scaledPadding = Math.round(20 * this.scaleFactor)
        return {
          padding: `${scaledPadding}px`,
        }
      },
      keypadButtonStyle() {
        const borderColor =
          this.buttonBorderColor ||
          this.baseData.colors?.passcodeV2?.buttonBorderColor ||
          this.accentColor
        const shadowColor = this.hexToRgba(borderColor, 0.35)
        // Increased button size from 68 to 88 to utilize extra space
        const scaledSize = Math.round(88 * this.scaleFactor)
        const scaledFontSize = Math.round(40 * this.scaleFactor)
        const scaledMargin = Math.round(8 * this.scaleFactor)
        const scaledBorderWidth = Math.round(2 * this.scaleFactor)
        const scaledShadowBlur = Math.round(12 * this.scaleFactor)
        const scaledShadowY = Math.round(3 * this.scaleFactor)

        return {
          width: `${scaledSize}px`,
          height: `${scaledSize}px`,
          background:
            this.buttonBackground ||
            this.baseData.colors?.passcodeV2?.buttonBackgroundColor ||
            '#fff',
          color:
            this.buttonTextColor ||
            this.baseData.colors?.passcodeV2?.buttonTextColor ||
            this.accentColor,
          borderColor,
          borderWidth: `${scaledBorderWidth}px`,
          borderStyle: 'solid',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: `0 ${scaledMargin}px`,
          fontSize: `${scaledFontSize}px`,
          fontWeight: 500,
          boxShadow: `0 ${scaledShadowY}px ${scaledShadowBlur}px ${shadowColor}`,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'translateY(0) scale(1)',
        }
      },
      keypadButtonTextStyle() {
        const scaledFontSize = Math.round(40 * this.scaleFactor)
        return {
          color:
            this.buttonTextColor ||
            this.baseData.colors?.passcodeV2?.buttonTextColor ||
            this.accentColor,
          fontWeight: 600,
          fontSize: `${scaledFontSize}px`,
        }
      },
      keypadButtonHoverStyle() {
        const borderColor =
          this.buttonBorderColor ||
          this.baseData.colors?.passcodeV2?.buttonBorderColor ||
          this.accentColor
        const shadowColor = this.hexToRgba(borderColor, 0.6)
        const scaledTranslateY = Math.round(-3 * this.scaleFactor)
        const scaledShadowY = Math.round(6 * this.scaleFactor)
        const scaledShadowBlur = Math.round(25 * this.scaleFactor)
        return {
          transform: `translateY(${scaledTranslateY}px)`,
          boxShadow: `0 ${scaledShadowY}px ${scaledShadowBlur}px ${shadowColor}`,
          scale: '1.05',
        }
      },
      keypadButtonActiveStyle() {
        const borderColor =
          this.buttonBorderColor ||
          this.baseData.colors?.passcodeV2?.buttonBorderColor ||
          this.accentColor
        const shadowColor = this.hexToRgba(borderColor, 0.7)
        const scaledTranslateY = Math.round(2 * this.scaleFactor)
        const scaledShadowY = Math.round(2 * this.scaleFactor)
        const scaledShadowBlur = Math.round(6 * this.scaleFactor)
        return {
          transform: `translateY(${scaledTranslateY}px)`,
          boxShadow: `0 ${scaledShadowY}px ${scaledShadowBlur}px ${shadowColor}`,
          scale: '0.96',
        }
      },
    },
    watch: {
      showPasscodeV2FieldsPreview(val) {
        if (!val) {
          this.$eventHub.$emit('showPasscodev2', false)
        }
      },
      passcode(newVal) {
        this.$v.passcode.$touch()

        if (newVal && newVal.length === this.passcodeLength) {
          this.autoConnect()
        }
      },
    },
    created() {
      this.updatePasscodeLength()
    },
    mounted() {
      document.addEventListener('keydown', this.handleKeydown)
      window.addEventListener('resize', this.calculateScale)
      this.calculateScale()
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.handleKeydown)
      window.removeEventListener('resize', this.calculateScale)
    },
    validations() {
      return {
        passcode: {
          required,
          correctLength: (value) => {
            if (!value) return true
            return value.length === this.passcodeLength
          },
        },
      }
    },
    methods: {
      calculateScale() {
        this.viewportHeight = window.innerHeight

        // Updated required height calculation:
        // - Button size increased from 68 to 80 (adds ~36px for 3 rows)
        // - Title and dots are now static (no longer scale)
        // - Reduced spacing between elements
        const requiredHeight = 40 + 30 + 360 + 60 + 40 + 40

        const availableHeight = this.viewportHeight * 0.65

        const calculatedScale = Math.min(availableHeight / requiredHeight, 1)

        let finalScale = calculatedScale
        if (this.viewportHeight < 500) {
          finalScale = calculatedScale * 0.85
        } else if (this.viewportHeight < 600) {
          finalScale = calculatedScale * 0.9
        } else if (this.viewportHeight < 700) {
          finalScale = calculatedScale * 0.95
        }

        this.scaleFactor = Math.max(finalScale, 0.4)
      },
      hexToRgba(hex, alpha = 1) {
        hex = hex.replace('#', '')

        const r = parseInt(hex.substr(0, 2), 16)
        const g = parseInt(hex.substr(2, 2), 16)
        const b = parseInt(hex.substr(4, 2), 16)

        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      },
      updatePasscodeLength() {
        const passcodeOption = this.baseData?.loginOptions?.options?.find(
          (a) => a.name === 'Passcode V2',
        )
        if (passcodeOption?.codeLength) {
          this.passcodeLength = passcodeOption.codeLength
        }
      },
      goBack() {
        this.$eventHub.$emit('showPasscodev2', false)
      },
      delayTouch($v) {
        $v.$reset()
        if (touchMap.has($v)) {
          clearTimeout(touchMap.get($v))
        }
        touchMap.set($v, setTimeout($v.$touch, 1000))
      },
      connect() {
        if (this.api.includes('preview')) return

        if (this.allValidated) {
          this.connectNow()
          return
        }

        if (this.passcode.length < this.passcodeLength) {
          this.handleIncompletePasscode()
          return
        }

        this.validating = true
        this.allValid()
      },
      allValid() {
        this.delayTouch(this.$v.passcode)
        setTimeout(() => {
          this.validating = false
        }, 1000)
        return !this.$v.$error && this.$v.$dirty
      },
      connectNow() {
        this.loading = true
        this.backendError = false
        this.backendErrorMsg = ''
        axios
          .post(
            this.api + '&action=' + this.action,
            qs.stringify({ wftkn: this.wftkn, passcode: this.passcode }),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
          )
          .then((res) => {
            if (!res.data.status) {
              if (res.data.message === 'passcode_error') {
                this.handlePasscodeError()
                return
              } else {
                this.backendError = true
                this.backendErrorMsg = res.data.message || this.$t('something_went_wrong')
              }
              this.connected = false
              this.resetPasscode()
            } else {
              this.connected = true
              setTimeout(() => {
                window.location = decodeURIComponent(res.data.url)
              }, 1000)
            }
          })
          .catch((error) => {
            this.loading = false
            const errorMessage = error.response?.data?.message
            if (errorMessage === 'passcode_error') {
              this.handlePasscodeError()
              return
            } else {
              this.backendError = true
              this.backendErrorMsg = errorMessage || this.$t('something_went_wrong')
            }
            this.connected = false
            this.resetPasscode()
          })
      },
      handleNumberClick(number) {
        if (this.enteredPasscode.length < this.passcodeLength) {
          this.enteredPasscode.push(number)
          this.passcode = this.enteredPasscode.join('')
          this.invalidPasscode = false
          this.invalidPasscodeMSG = ''
          this.backendError = false
          this.backendErrorMsg = ''
          this.$v.passcode.$touch()
        }
      },
      autoConnect() {
        setTimeout(() => {
          if (this.allValidated && !this.loading) {
            this.connectNow()
          }
        }, 300)
      },
      handleKeydown(event) {
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

        const key = event.key

        if (/^[0-9]$/.test(key)) {
          event.preventDefault()
          const number = parseInt(key)

          this.hoveredNumber = number

          this.handleNumberClick(number)

          setTimeout(() => {
            this.hoveredNumber = null
          }, 400)
        } else if (key === 'Backspace') {
          event.preventDefault()
          this.handleBackspace()
        } else if (key === 'Enter') {
          event.preventDefault()
          if (this.allValidated) {
            this.connect()
          }
        }
      },
      handleBackspace() {
        if (this.enteredPasscode.length > 0) {
          this.enteredPasscode.pop()
          this.passcode = this.enteredPasscode.join('')
          this.invalidPasscode = false
          this.invalidPasscodeMSG = ''
          this.backendError = false
          this.backendErrorMsg = ''
          this.$v.passcode.$touch()
        }
      },
      isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0
      },
      handlePress(number) {
        this.pressedNumber = number
      },
      handleRelease(number) {
        if (this.pressedNumber === number) {
          this.pressedNumber = null
        }
      },
      handleHover(num) {
        this.hoveredNumber = num
      },
      handleHoverLeave(num) {
        this.hoveredNumber = null
      },
      getKeypadButtonStyle(num) {
        const baseStyle = this.keypadButtonStyle

        if (this.pressedNumber === num) {
          return { ...baseStyle, ...this.keypadButtonActiveStyle }
        }

        if (this.hoveredNumber === num) {
          return { ...baseStyle, ...this.keypadButtonHoverStyle }
        }

        return baseStyle
      },
      handlePasscodeError() {
        this.resetPasscode()
        this.loading = false

        this.shakeDots = true
        setTimeout(() => {
          this.shakeDots = false
        }, 500)

        setTimeout(() => {
          this.invalidPasscode = true
          this.invalidPasscodeMSG =
            this.$t('passcode_error') || 'Incorrect passcode. Please try again.'
        }, 100)
      },
      handleIncompletePasscode() {
        this.shakeDots = true
        setTimeout(() => {
          this.shakeDots = false
        }, 500)

        setTimeout(() => {
          this.invalidPasscode = true
        }, 100)
      },
      resetPasscode() {
        this.passcode = ''
        this.enteredPasscode = []
        this.invalidPasscode = false
        this.invalidPasscodeMSG = ''
        this.backendError = false
        this.backendErrorMsg = ''
        this.$v.passcode.$reset()
      },
    },
  }
</script>

<style scoped>
  .universal-passcode-bg {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    background: transparent;
    outline: none;
    min-height: 100vh;
    box-sizing: border-box;
    overflow-y: auto;
    margin-top: -25px;
  }
  .universal-passcode-bg:focus {
    outline: none;
  }
  .universal-passcode-title {
    color: #222;
    font-weight: 600;
    text-align: center;
    margin-top: -15px;
  }

  .passcode-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 4px;
  }
  .universal-passcode-dots {
    display: flex;
    justify-content: center;
  }

  .universal-passcode-dots.shake {
    animation: shake 0.5s ease-in-out;
  }

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    10%,
    30%,
    50%,
    70%,
    90% {
      transform: translateX(-5%);
    }
    20%,
    40%,
    60%,
    80% {
      transform: translateX(5%);
    }
  }
  .universal-dot {
    border-radius: 50%;
    border-style: solid;
    background: transparent;
    transition: background 0.2s, border 0.2s;
  }
  .universal-dot.filled {
    background: currentColor;
  }
  .universal-keypad {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .universal-keypad-row {
    display: flex;
  }
  .universal-key {
    border-radius: 50%;
    border-width: 2px;
    border-style: solid;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: transparent;
    cursor: pointer;
    user-select: none;
    position: relative;
  }
  .universal-key.empty {
    border: none;
    background: transparent;
    pointer-events: none;
  }
  .universal-key-number {
    font-weight: 600;
    line-height: 1;
  }
  .error-message {
    color: #ff4444;
    font-size: 12px;
    text-align: center;
    width: 100%;
    height: 16px;
    line-height: 16px;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
  }
  .error-message.show {
    opacity: 1;
    visibility: visible;
  }
  .error-msg-area {
    margin-bottom: 16px;
  }

  .social-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateY(0);
    position: relative;
  }

  .social-btn:hover {
    transform: translateY(-1px);
    box-shadow: 1px 3px 6px 1px rgba(0, 0, 0, 0.15);
  }

  .social-btn:active {
    transform: translateY(0);
    box-shadow: 0px 1px 3px 1px rgba(0, 0, 0, 0.25);
    transition: all 0.1s ease-in-out;
  }

  .social-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: translateY(0);
  }

  .social-btn:disabled:hover {
    opacity: 0.7;
    transform: translateY(0);
    box-shadow: inherit;
  }
</style>
